import { ActionEnum, LogObject, ResultEnum, TypeTargetEnum } from '../../log-fonctionnel/log-fonctionnel.service';
import { DmpHlDeleteDocumentParams } from 'src/app/utils/interfaces/new/dmp-hl-delete-document-params.interface';

/**
 * Construction des entrées du journal fonctionnel (DomainEnum.Dmp) liées à l'envoi et à la suppression de documents,
 * partagée par les stratégies websocket et REST. L'émission (_addLog) reste propre à chaque stratégie.
 */

/** Extrait du ou des documents envoyés les seuls champs à journaliser (pas de contenu base64) */
function _getSendDocLogData(dataSent: any): any {
  return Array.isArray(dataSent)
    ? dataSent.map((d) => ({
        documentTitle: d.s_title,
        documentId: d.documentId,
        documentIdDmp: d.s_uniqueId,
        documentTypeCode: d.s_typeCode,
        documentClassCode: d.s_classCode,
        documentFormat: d.i_format,
        documentFormatDescription: d.s_documentFormatDescription,
        documentVisibility: d.i_visibility,
        auteurs: d.authors,
        dateCreation: d.dateCreation,
        vaccinInformation: d.vaccinInformation,
      }))
    : dataSent
      ? {
          documentTitle: dataSent.s_title,
          documentId: dataSent.documentId,
          s_uniqueId: dataSent.s_uniqueId,
          category: dataSent.s_category,
          visibility: dataSent.i_visibility,
          format: dataSent.i_format,
          description: dataSent.s_description,
          auteurs: dataSent.authors,
          dateCreation: dataSent.dateCreation,
          vaccinInformation: dataSent.vaccinInformation,
        }
      : dataSent;
}

function _getSendDocLogTitle(safeData: any): string {
  return Array.isArray(safeData) && safeData.length > 0
    ? safeData.map(d => d.documentTitle).join(', ')
    : safeData.documentTitle;
}

export function buildSendDocErrorLog(err: any, dataSent: any, patientId?: string, isReplacement = false): LogObject {
  const safeData = _getSendDocLogData(dataSent);
  const titleStr = _getSendDocLogTitle(safeData);

  return {
    action: isReplacement ? ActionEnum.Update : ActionEnum.Create,
    result: ResultEnum.Error,
    typeTarget: TypeTargetEnum.Patient,
    idTarget: patientId,
    subDomain: isReplacement ? 'Remplacement document au DMP' : 'Envoi document au DMP',
    internals: {error: err, data: safeData},
    details: isReplacement
      ? `Échec du remplacement du document: ${titleStr}`
      : `Échec de l'envoi du document: ${titleStr}`,
  };
}

export function buildSendDocSuccessLog(rtd: any, dataSent?: any, patientId?: string, isReplacement = false): LogObject {
  const safeData = _getSendDocLogData(dataSent);
  const titleStr = _getSendDocLogTitle(safeData);

  return {
    action: isReplacement ? ActionEnum.Update : ActionEnum.Create,
    result: ResultEnum.Success,
    typeTarget: TypeTargetEnum.Patient,
    idTarget: patientId,
    subDomain: isReplacement ? 'Remplacement document au DMP' : 'Envoi document au DMP',
    internals: {returned: rtd, data: safeData},
    details: isReplacement
      ? `Succès du remplacement du document: ${titleStr}`
      : `Succès de l'envoi du document: ${titleStr}`,
  };
}

/** Journal de suppression d'un document (isArchive = false) ou d'une archive (isArchive = true) du DMP */
export function buildDeleteDocLog(
  result: ResultEnum,
  deleteParams: DmpHlDeleteDocumentParams,
  patientId?: string,
  error?: any,
  isArchive = false
): LogObject {
  const target = isArchive ? 'de l\'archive DMP' : 'du document DMP';
  const docLabel = deleteParams.s_title ? ': ' + deleteParams.s_title : deleteParams.s_uniqueId;
  const isError = result === ResultEnum.Error;

  return {
    action: ActionEnum.Delete,
    result,
    typeTarget: TypeTargetEnum.Patient,
    idTarget: patientId,
    subDomain: isArchive ? 'Suppression archive DMP' : 'Suppression document DMP',
    internals: isError ? { error, deleteParams } : { deleteParams },
    details: `${isError ? 'Échec' : 'Succès'} de la suppression ${target} ${docLabel}`
  };
}
